import React from "react";
import {
  Easing,
  interpolate,
  spring,
  useCurrentFrame,
} from "remotion";
import { theme } from "../../brand";
import { COPY } from "../../b2c/copy";
import { FLOAT_SHADOW, LOOK } from "../../b2c/look";
import { FrakLogo } from "../../components/FrakLogo";
import { ReelFrame } from "../ui/ReelFrame";
import { TICKET } from "./copy";

/**
 * Reel "Le ticket" (9:16, silent, ~13s): a receipt of everything you
 * recommended this year prints line by line, every line 0,00 €, TOTAL
 * 0,00 €. A Frak stamp slams on it, every amount flips to a (sample) gain
 * and the total counts up. End card: the landing's "ton influence mérite
 * d'être rémunérée", free / no followers / real cash, the stores.
 *
 * Frame 0 is the cover (hook + receipt header already there). Silent: the
 * trending sound is added in Instagram, so nothing is beat-locked.
 */

export const TICKET_FPS = 30;

/** Timeline, in frames. Holds are reading time: never shorten one. */
const T = {
  /** Print time of each line: three readable ones, then a burst. */
  items: [12, 26, 40, 50, 58, 65, 71, 76, 81, 86],
  total: 100,
  /** The stamp falls from `stamp` and hits the paper on `land`. */
  stamp: 150,
  land: 158,
  /** First amount flips, then one every `flipStagger`. */
  flip: 166,
  flipStagger: 3,
  count: [196, 222],
  note: 206,
  exit: 275,
  end: 288,
  duration: 400,
} as const;

export const TICKET_DURATION = T.duration;

if (T.items.length !== TICKET.items.length) {
  throw new Error("Ticket reel: one print time per item in T.items.");
}

/** Receipt geometry (logical px, 1080×1920). Text stays clear of the Reels rail. */
const P = {
  left: 170,
  top: 470,
  width: 740,
  pad: 48,
  head: 160,
  row: 58,
  totalRow: 132,
  note: 56,
  tooth: 22,
};

const GREEN = theme.brand.colors.success[800];
const SUM = TICKET.items.reduce((s, i) => s + i.gain, 0);
const money = COPY.fr.money;

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/** 0 → 1 over `len` frames from `at`, eased out (paper feed, ink, flips). */
const ramp = (frame: number, at: number, len: number) =>
  interpolate(frame, [at, at + len], [0, 1], {
    ...clamp,
    easing: Easing.out(Easing.cubic),
  });

/** Thermal-print look: the ink appears left to right. */
const Ink: React.FC<{ at: number; children: React.ReactNode }> = ({
  at,
  children,
}) => {
  const p = ramp(useCurrentFrame(), at, 5);
  return (
    <div style={{ clipPath: `inset(-20% ${(1 - p) * 100}% -20% 0)` }}>
      {children}
    </div>
  );
};

const Arrow: React.FC = () => (
  <svg
    width={28}
    height={16}
    viewBox="0 0 28 16"
    style={{ margin: "0 14px", flexShrink: 0 }}
  >
    <path
      d="M2 8H25M18 2l7 6-7 6"
      fill="none"
      stroke={LOOK.muted}
      strokeWidth={2.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** An amount that rolls from 0,00 € to the gain, in a fixed-width slot. */
const Amount: React.FC<{ gain: number; at: number }> = ({ gain, at }) => {
  const p = ramp(useCurrentFrame(), at, 7);
  const cell: React.CSSProperties = {
    position: "absolute",
    right: 0,
    top: 0,
    height: P.row,
    lineHeight: `${P.row}px`,
  };
  return (
    <div
      style={{
        position: "relative",
        width: 190,
        height: P.row,
        overflow: "hidden",
        fontWeight: LOOK.bold,
        fontVariantNumeric: "tabular-nums",
        flexShrink: 0,
      }}
    >
      <span style={{ ...cell, color: LOOK.ink, translate: `0 ${-p * 100}%` }}>
        {money(0)}
      </span>
      <span style={{ ...cell, color: GREEN, translate: `0 ${(1 - p) * 100}%` }}>
        +{money(gain)}
      </span>
    </div>
  );
};

const Receipt: React.FC = () => {
  const frame = useCurrentFrame();

  // The paper grows as it prints: one row per item, then total, then note.
  const height =
    P.pad +
    P.head +
    T.items.reduce((h, at) => h + ramp(frame, at, 4) * P.row, 0) +
    ramp(frame, T.total, 5) * P.totalRow +
    ramp(frame, T.note, 5) * P.note +
    P.pad / 2;

  // Damped jolt when the stamp hits.
  const t = frame - T.land;
  const jolt = t < 0 ? 0 : 12 * Math.exp(-t / 3) * Math.cos(t * 1.3);

  const fall = interpolate(frame, [T.stamp, T.land], [2.4, 1], {
    ...clamp,
    easing: Easing.in(Easing.quad),
  });
  const stampIn = interpolate(frame, [T.stamp, T.stamp + 3], [0, 1], clamp);

  const value = interpolate(frame, T.count, [0, SUM], {
    ...clamp,
    easing: Easing.out(Easing.cubic),
  });
  const counting = frame >= T.count[0];
  const pop = spring({
    frame: frame - T.count[1],
    fps: TICKET_FPS,
    config: { damping: 9, stiffness: 220, mass: 0.6 },
  });
  const settle = counting ? 1 + 0.08 * Math.sin(Math.PI * Math.min(pop, 1)) : 1;

  // Exit: the receipt drops out of frame before the end card.
  const drop = interpolate(frame, [T.exit, T.exit + 16], [0, 1500], {
    ...clamp,
    easing: Easing.in(Easing.cubic),
  });

  const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    height: P.row,
    fontFamily: LOOK.body,
    fontSize: 36,
    color: LOOK.ink,
  };

  return (
    <div
      style={{
        position: "absolute",
        left: P.left,
        top: P.top,
        width: P.width,
        translate: `0 ${jolt + drop}px`,
      }}
    >
      <div
        style={{
          position: "relative",
          height,
          background: LOOK.white,
          borderRadius: "8px 8px 0 0",
          boxShadow: FLOAT_SHADOW,
          overflow: "hidden",
          padding: `${P.pad}px ${P.pad}px 0`,
        }}
      >
        {/* Header (printed before frame 0: the cover). */}
        <div style={{ height: P.head }}>
          <div
            style={{
              fontFamily: LOOK.body,
              fontWeight: LOOK.semi,
              fontSize: 24,
              letterSpacing: 4,
              color: LOOK.muted,
            }}
          >
            {TICKET.kicker}
          </div>
          <div
            style={{
              fontFamily: LOOK.display,
              fontWeight: LOOK.bold,
              fontSize: 56,
              letterSpacing: -2,
              color: LOOK.ink,
              marginTop: 8,
            }}
          >
            {TICKET.title}
          </div>
          <div
            style={{
              borderTop: `3px dashed ${LOOK.hairline}`,
              marginTop: 20,
            }}
          />
        </div>

        {TICKET.items.map((item, i) => (
          <Ink key={item.what} at={T.items[i]}>
            <div style={rowStyle}>
              <span style={{ fontWeight: LOOK.semi }}>{item.what}</span>
              <Arrow />
              <span style={{ color: LOOK.inkSoft }}>{item.who}</span>
              <span
                style={{
                  flex: 1,
                  alignSelf: "flex-end",
                  margin: "0 14px 16px",
                  borderBottom: `3px dotted ${LOOK.hairline}`,
                }}
              />
              <Amount gain={item.gain} at={T.flip + i * T.flipStagger} />
            </div>
          </Ink>
        ))}

        <Ink at={T.total}>
          <div
            style={{
              borderTop: `3px dashed ${LOOK.hairline}`,
              marginTop: 18,
              height: P.totalRow - 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontFamily: LOOK.display,
              fontWeight: LOOK.bold,
            }}
          >
            <span style={{ fontSize: 44, color: LOOK.ink }}>
              {TICKET.total}
            </span>
            <span
              style={{
                fontSize: 76,
                letterSpacing: -2,
                fontVariantNumeric: "tabular-nums",
                color: counting ? GREEN : LOOK.ink,
                scale: settle,
                transformOrigin: "right center",
              }}
            >
              {counting ? "+" : ""}
              {money(value)}
            </span>
          </div>
        </Ink>

        <Ink at={T.note}>
          <div
            style={{
              height: P.note,
              fontFamily: LOOK.body,
              fontSize: 28,
              color: LOOK.inkSoft,
              textAlign: "right",
            }}
          >
            {TICKET.note}
          </div>
        </Ink>
      </div>

      {/* Torn bottom edge. */}
      <svg
        width={P.width}
        height={P.tooth}
        viewBox={`0 0 ${P.width} ${P.tooth}`}
        style={{ display: "block" }}
      >
        <path
          d={
            Array.from({ length: P.width / 20 + 1 }, (_, k) =>
              `${k === 0 ? "M" : "L"}${k * 20} 0 L${k * 20 + 10} ${P.tooth}`,
            ).join(" ") + ` L${P.width} 0 Z`
          }
          fill={LOOK.white}
        />
      </svg>

      {/* The stamp lands on the empty right side of the header, above the
          paper (not clipped by it while it falls). */}
      {frame >= T.stamp ? (
          <div
            style={{
              position: "absolute",
              right: 44,
              top: 40,
              opacity: stampIn,
              scale: fall,
              rotate: "-10deg",
              border: `7px solid ${LOOK.blue}`,
              borderRadius: 22,
              padding: "12px 26px 18px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              background: LOOK.white,
            }}
          >
            <span
              style={{
                fontFamily: LOOK.display,
                fontWeight: LOOK.bold,
                fontSize: 30,
                letterSpacing: 8,
                color: LOOK.blue,
              }}
            >
              {TICKET.stamp}
            </span>
            <FrakLogo width={200} />
          </div>
        ) : null}
    </div>
  );
};

export const TicketReel: React.FC = () => (
  <ReelFrame
    hook={TICKET.hook}
    exitAt={T.exit}
    end={{ at: T.end, line: TICKET.end.line, sub: TICKET.end.sub }}
  >
    <Receipt />
  </ReelFrame>
);
